import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { SolicitacaoService, type SolicitacaoResponseDto } from '@/services'
import { getGuestToken, setGuestToken } from '@/lib/http-client'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'
import { Loader2 } from 'lucide-react'
import { TrackingView } from '@/components/solicitacao/tracking-view'
import { Logo } from '@/components/core/logo'
import { TrackingSkeleton } from '@/components/solicitacao/tracking-skeleton'
import { useSolicitacaoUpdates } from '@/hooks/useSolicitacaoUpdates'
import { extractOrderId } from '@/lib/utils'

export const Route = createFileRoute('/shared/$solicitacaoId/acompanhar')({
  component: ShareSolicitacaoPage,
})

const formSchema = z.object({
  phoneLast4: z.string().length(4, {
    message: 'Digite os 4 últimos dígitos do seu telefone',
  }),
})

function ShareSolicitacaoPage() {
  const { solicitacaoId: rawId } = Route.useParams()
  const entregaId = Number(extractOrderId(rawId))
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const [isAuthorized, setIsAuthorized] = useState(false)

  const [solicitacao, setSolicitacao] = useState<SolicitacaoResponseDto | null>(null)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      phoneLast4: '',
    },
  })

  useEffect(() => {
    const fetchSolicitacao = async () => {
      if (!entregaId || isNaN(entregaId)) {
        setIsCheckingAuth(false)
        return
      }

      try {
        const response = await SolicitacaoService.track(entregaId, {
          skipAuthRedirect: true,
          authToken: getGuestToken() ?? undefined,
        })

        setSolicitacao(response)
        setIsAuthorized(true)
      } catch (error) {
        setIsAuthorized(false)
      } finally {
        setIsCheckingAuth(false)
      }
    }
    fetchSolicitacao()
  }, [entregaId])

  // CRITICAL FIX: Move useSolicitacaoUpdates BEFORE any conditional returns
  // This ensures the same number of hooks are called in every render
  useSolicitacaoUpdates(
    isAuthorized ? solicitacao?.updateChannel : null,
    isAuthorized ? entregaId : undefined,
    isAuthorized && solicitacao
      ? (updatedData) => {
          setSolicitacao((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              ...(updatedData.status && { status: updatedData.status }),
              ...(updatedData.valor_entrega && { valor_entrega: updatedData.valor_entrega }),
              ...(updatedData.formaPagamento && { formaPagamento: updatedData.formaPagamento }),
            };
          });
        }
      : undefined
  )

  useEffect(() => {
    if (!solicitacao) return
    const finalStatuses = ['delivered', 'canceled', 'rejected']
    if (finalStatuses.includes(solicitacao.status)) {
      setGuestToken(null)
    }
  }, [solicitacao?.status])

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true)
    try {
      const response = await SolicitacaoService.identify(Number(entregaId), {
        phoneLast4: values.phoneLast4,
      })

      setGuestToken(response.token)

      const data = await SolicitacaoService.track(Number(entregaId), {
        authToken: response.token,
      })

      setSolicitacao(data)
      setIsAuthorized(true)
    } catch (error) {
      console.error(error)
      form.setError('root', {
        message: 'Não foi possível identificar. Verifique os dados e tente novamente.',
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (isCheckingAuth) {
    return <TrackingSkeleton />
  }

  if (isAuthorized) {
    return (
      <TrackingView
        solicitacao={solicitacao!}
        sholdShowBackButton={false}
        onBack={() => navigate({ to: '/' })}
      />
    )
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `url("/authentication-bg.svg")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="w-full max-w-md relative z-10">
        <div className="bg-card rounded-4xl p-8">
          <div className="flex items-center justify-center mb-6">
            <Logo className="h-12" isTraditionalLogo />
          </div>

          <div className="text-center space-y-4 mb-8">
            <div className="space-y-2">
              <h1 className="text-xl font-bold text-foreground">
                Acompanhar Entrega
              </h1>
              <p className="text-sm text-muted-foreground">
                Você foi convidado para acompanhar esta entrega. Para sua segurança, confirme os <strong>4 últimos dígitos</strong> do seu telefone.
              </p>
            </div>

            <div className="flex justify-center">
              <div className="bg-muted/50 px-4 py-2 rounded-lg border border-border/50">
                <p className="text-sm font-mono text-muted-foreground tracking-widest">
                  (XX) XXXXX-<span className="text-primary font-bold">••••</span>
                </p>
              </div>
            </div>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="phoneLast4"
                render={({ field }) => (
                  <FormItem className="flex flex-col items-center space-y-4">
                    <FormLabel className="sr-only">
                      4 últimos dígitos do telefone
                    </FormLabel>
                    <FormControl>
                      <InputOTP
                        maxLength={4}
                        {...field}
                      >
                        <InputOTPGroup className="gap-3">
                          {Array.from({ length: 4 }).map((_, index) => (
                            <InputOTPSlot
                              key={index}
                              index={index}
                              className="w-14 h-16 text-2xl rounded-xl border-input bg-background ring-offset-background transition-all duration-200 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:border-ring"
                            />
                          ))}
                        </InputOTPGroup>
                      </InputOTP>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.formState.errors.root && (
                <div className="text-sm text-destructive text-center bg-destructive/10 p-3 rounded-lg border border-destructive/20">
                  {form.formState.errors.root.message}
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-10 text-base font-medium rounded-xl"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  'Acessar Entrega'
                )}
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </div>
  )
}
